import { useState } from 'react';
import { BookIcon, CrossIcon, SearchIcon } from 'assets/icons';
import { classnames } from 'helpers/utils';
import { ContentThumbnail } from 'common/content-thumbnail';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './vault-search-bar.module.scss';

export interface SearchBarChip {
  label: string;
  // First image/video memory in the album — rendered as the chip thumbnail.
  // When absent, a default album graphic is shown.
  thumbnailContent?: AlbumContentSerialized;
  // Leaving the chip's context. When set, hovering the chip turns the
  // thumbnail into an X that calls this.
  onRemove?: () => void;
}

interface VaultSearchBarProps {
  value?: string;
  placeholder?: string;
  className?: string;
  onSearch?: (value: string) => void;
  // Context chip (e.g. the open album) shown to the left of the input.
  chip?: SearchBarChip;
}

export const VaultSearchBar: React.FC<VaultSearchBarProps> = ({
  value,
  placeholder = 'Search Albums',
  className = '',
  onSearch,
  chip,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [text, setText] = useState(value || '');

  const handleChange = (newValue: string) => {
    setText(newValue);
    onSearch?.(newValue);
  };

  const handleClear = () => {
    setText('');
    onSearch?.('');
  };
  return (
    <div
      className={classnames(
        styles.container,
        {
          [styles.focused]: isFocused,
        },
        className,
      )}
    >
      {chip && (
        <div className={styles.chip}>
          <span className={styles.chipLead}>
            <span className={styles.chipThumb}>
              {chip.thumbnailContent ? (
                <ContentThumbnail content={chip.thumbnailContent} />
              ) : (
                <span className={styles.chipDefault}>
                  <BookIcon />
                </span>
              )}
            </span>
            {chip.onRemove && (
              <button
                type="button"
                className={styles.chipClose}
                onClick={chip.onRemove}
                aria-label={`Leave ${chip.label}`}
              >
                <CrossIcon />
              </button>
            )}
          </span>
          <span className={styles.chipLabel}>{chip.label}</span>
        </div>
      )}
      {text ? (
        <button
          type="button"
          onClick={handleClear}
          className={styles.crossIcon}
        >
          <CrossIcon />
        </button>
      ) : (
        !chip && <SearchIcon className={styles.searchIcon} />
      )}

      <input
        type="text"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className={classnames(styles.input)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  );
};
